import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sun, Droplet, Snowflake, PawPrint, Ruler, ArrowLeft, Check, Truck, Store, ShieldCheck,
} from "lucide-react";
import { getPlantBySlug, getRelated } from "../../data/plants";
import { getCareGuide } from "../../data/careGuides";
import { reviewsFor, ratingSummary } from "../../data/reviews";
import { useCart } from "../../context/CartContext";
import { euro, formatDate } from "../../utils/format";
import PlantImage from "../../components/fancyplants/PlantImage";
import ProductCard from "../../components/fancyplants/ProductCard";
import Stars from "../../components/fancyplants/Stars";
import Seo from "../../components/fancyplants/Seo";
import { Trait, NotifyMe, EmptyState } from "../../components/fancyplants/states";
import { abs } from "../../config/site";

const LIGHT_LABEL = {
  "bright-indirect": "Bright, indirect",
  "bright-direct": "Bright / direct sun",
  "low-light": "Low-light tolerant",
};
const SIZE_LABEL = { small: "Small", medium: "Medium", large: "Statement size" };

export default function ProductPage() {
  const { slug } = useParams();
  const plant = getPlantBySlug(slug);
  const { addItem } = useCart();
  const [activeImg, setActiveImg] = useState(0);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    setAdded(false);
  }, [slug]);

  if (!plant) {
    return (
      <div className="container-fp pt-32 pb-20">
        <Seo title="Plant not found" noindex />
        <EmptyState
          title="We couldn't find that plant"
          message="It may have sold out and been retired, or the link is mistyped."
          action={<Link to="/shop" className="btn-forest">Back to the shop</Link>}
        />
      </div>
    );
  }

  const guide = getCareGuide(plant.careGuideId);
  const reviews = reviewsFor(plant.id);
  const summary = ratingSummary(plant.id);
  const related = getRelated(plant);
  const outOfStock = plant.stock <= 0;
  const lowStock = plant.stock > 0 && plant.stock <= 3;
  const gallery = [...plant.images, plant.arrivesShot];

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: plant.name,
    image: gallery,
    description: plant.blurb,
    category: plant.category,
    brand: { "@type": "Brand", name: "Fancy Plants" },
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      price: plant.price,
      availability: outOfStock
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
      url: abs(`/shop/${plant.slug}`),
    },
    ...(summary.count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: summary.avg,
            reviewCount: summary.count,
          },
        }
      : {}),
  };

  const handleAdd = () => {
    addItem(plant, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="pt-24">
      <Seo
        title={plant.name}
        description={plant.blurb}
        path={`/shop/${plant.slug}`}
        image={plant.images[0]}
        type="product"
        jsonLd={productLd}
      />
      <div className="container-fp py-4">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-forest-700">
          <ArrowLeft size={16} /> All plants
        </Link>
      </div>

      <div className="container-fp grid gap-10 py-6 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <PlantImage
            src={gallery[activeImg]}
            alt={`${plant.name} — ${plant.latinName}`}
            ratio="4/5"
            className="rounded-3xl"
            priority
          />
          <div className="mt-3 flex gap-3">
            {gallery.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`relative w-20 overflow-hidden rounded-xl border-2 transition-colors ${
                  activeImg === i ? "border-brass" : "border-transparent"
                }`}
                aria-label={`View image ${i + 1}`}
              >
                <PlantImage src={src} alt="" ratio="1/1" />
                {i === gallery.length - 1 && (
                  <span className="absolute inset-x-0 bottom-0 bg-green-900/70 py-0.5 text-center text-[9px] font-medium text-canvas">
                    What arrives
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Buy box */}
        <div>
          <p className="eyebrow mb-2">{plant.category}</p>
          <h1 className="t-h1">{plant.name}</h1>
          <p className="mt-1 text-lg italic text-ink-soft">{plant.latinName}</p>

          {summary.count > 0 && (
            <Link to="#reviews" className="mt-3 inline-flex items-center gap-2 text-sm">
              <Stars value={summary.avg} />
              <span className="text-ink-soft">
                {summary.avg} · {summary.count} verified review{summary.count === 1 ? "" : "s"}
              </span>
            </Link>
          )}

          <p className="mt-5 text-lg leading-relaxed text-ink">{plant.blurb}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="t-price text-3xl">{euro(plant.price)}</span>
            {plant.compareAt && (
              <span className="text-lg text-ink-soft line-through">{euro(plant.compareAt)}</span>
            )}
          </div>

          {/* Add to cart / out-of-stock */}
          <div className="mt-6">
            {outOfStock ? (
              <div className="surface p-5">
                <NotifyMe plantName={plant.name} />
              </div>
            ) : (
              <>
                <button onClick={handleAdd} className="btn-brass w-full text-base sm:w-auto sm:min-w-[260px]">
                  {added ? (
                    <motion.span initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="inline-flex items-center gap-2">
                      <Check size={18} /> Added to basket
                    </motion.span>
                  ) : (
                    <>Add to basket — {euro(plant.price)}</>
                  )}
                </button>
                {lowStock && (
                  <p className="mt-2 text-sm font-medium text-terracotta">
                    Only {plant.stock} left — these go quickly.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Care stats */}
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-2">
            <Trait icon={Sun} label="Light" value={LIGHT_LABEL[plant.traits.light]} />
            <Trait
              icon={Droplet}
              label="Water"
              value={guide?.stats.water || "Moderate"}
            />
            <Trait
              icon={Snowflake}
              label="Placement"
              value={plant.traits.placement === "outdoor" ? "Hardy outdoor" : "Indoor"}
            />
            <Trait icon={Ruler} label="Size" value={SIZE_LABEL[plant.traits.size]} />
            <Trait
              icon={PawPrint}
              label="Pet safety"
              value={plant.traits.petSafe ? "Pet safe" : "Toxic if chewed"}
            />
            {guide && (
              <Trait icon={Snowflake} label="Hardiness" value={guide.stats.hardiness} />
            )}
          </div>

          {/* Fulfilment promise */}
          <div className="mt-7 overflow-hidden rounded-2xl bg-forest-100/60 text-sm">
            <div className="grid sm:grid-cols-[140px_1fr]">
              <PlantImage
                src={plant.arrivesShot}
                alt={`How ${plant.name} is packed before delivery`}
                ratio="4/3"
                className="h-full"
              />
              <div className="space-y-2 p-5">
            <p className="flex items-center gap-2 text-forest-900">
              <ShieldCheck size={18} className="text-forest-700" /> Packed with care, photographed before it ships.
            </p>
            <p className="flex items-center gap-2 text-forest-900">
              <Truck size={18} className="text-forest-700" /> Delivery across most of Ireland · free over €60.
            </p>
            <p className="flex items-center gap-2 text-forest-900">
              <Store size={18} className="text-forest-700" /> Or click &amp; collect from the midlands glasshouse.
            </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded care guide */}
      {guide && (
        <section className="bg-canvas-raised">
          <div className="container-fp py-14">
            <div className="mx-auto max-w-prose">
              <p className="eyebrow mb-2">Care guide</p>
              <h2 className="t-h2 mb-4">Looking after your {plant.name}</h2>
              <p className="t-lead mb-8">{guide.summary}</p>
              <div className="space-y-6">
                {guide.faq.map((item) => (
                  <div key={item.q} className="border-b border-line pb-6">
                    <h3 className="font-display text-xl leading-snug text-ink">{item.q}</h3>
                    <p className="mt-2 text-ink-soft">{item.a}</p>
                  </div>
                ))}
              </div>
              <Link to={`/care/${guide.slug}`} className="btn-outline mt-8">
                Read the full care guide
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section id="reviews" className="container-fp py-14">
        <div className="mb-8 flex items-center gap-3">
          <h2 className="t-h2">Verified reviews</h2>
          {summary.count > 0 && (
            <span className="flex items-center gap-2">
              <Stars value={summary.avg} /> <span className="text-ink-soft">{summary.avg}</span>
            </span>
          )}
        </div>
        {reviews.length === 0 ? (
          <p className="text-ink-soft">
            No reviews yet — be the first once your plant arrives. Only verified buyers can review,
            which is what keeps these honest.
          </p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {reviews.map((r) => (
              <article key={r.id} className="surface overflow-hidden">
                <PlantImage src={plant.arrivesShot} alt="" ratio="5/2" />
                <div className="p-5">
                <div className="mb-2 flex items-center justify-between">
                  <Stars value={r.rating} />
                  {r.verified && (
                    <span className="chip chip-active text-[11px]">
                      <Check size={12} /> Verified buyer
                    </span>
                  )}
                </div>
                <p className="text-ink">{r.body}</p>
                <p className="mt-3 rounded-lg bg-forest-100/50 px-3 py-2 text-sm text-forest-900">
                  <strong>On arrival:</strong> {r.deliveryConditionNote}
                </p>
                <p className="mt-3 text-sm text-ink-soft">
                  {r.author} · {formatDate(r.date)}
                </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="container-fp pb-20">
          <h2 className="t-h2 mb-8">More from {plant.category}</h2>
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.id} plant={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
